import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  X,
  MapPin,
  Clock,
} from 'lucide-react';
import ProviderLayout from '@/components/layout/ProviderLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import apiService from '@/services/api';

interface Job {
  id: number;
  booking_id: number;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  service: string;
  service_category?: string;
  address: string;
  postcode: string;
  booking_date: string;
  booking_time_start: string;
  booking_time_end?: string;
  estimated_duration: string;
  total_amount: string;
  notes: string;
  customer_notes: string;
  status: 'pending' | 'accepted' | 'in_progress' | 'completed' | 'cancelled' | 'rejected';
  created_at: string;
}

const ProviderSchedulePage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const hasCheckedAuth = useRef(false);

  useEffect(() => {
    if (!authLoading && hasCheckedAuth.current === false) {
      hasCheckedAuth.current = true;
      if (!user || user.role !== 'provider') {
        navigate('/provider/login');
      }
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (user?.id && !authLoading) {
      fetchJobs();
    }
  }, [user?.id, authLoading]);

    const fetchJobs = async () => {
    setLoading(true);
    try {
      // 🔧 FIXED: Use correct database status values (include assigned for newly assigned jobs)
      console.log('🔄 Fetching jobs with status filter: assigned,confirmed,in_progress');
      console.log(`📋 User info from auth:`, { userId: user?.id, role: user?.role });
      
      const response = await apiService.get(`/provider/jobs?status=assigned,confirmed,in_progress`);
      console.log('✅ Raw API Response:', response);
      console.log('📊 Response keys:', Object.keys(response));
      console.log('📊 Response.data type:', typeof response.data);
      console.log('📊 Response.data is array?:', Array.isArray(response.data));
      
      if (response.data && typeof response.data === 'object') {
        console.log('📊 Response.data keys:', Object.keys(response.data));
        if (!Array.isArray(response.data)) {
          console.log('📊 Response.data properties:', {
            jobs: Array.isArray(response.data.jobs) ? response.data.jobs.length : 'not array',
            accepted: Array.isArray(response.data.accepted) ? response.data.accepted.length : 'not array',
            in_progress: Array.isArray(response.data.in_progress) ? response.data.in_progress.length : 'not array',
            total: response.data.total,
            summary: response.data.summary,
          });
        }
      }
      
      if (response.success && response.data) {
        // First check if it's an empty array directly
        if (Array.isArray(response.data) && response.data.length === 0) {
          console.warn('⚠️ API returned empty array for data - this is unexpected!');
          console.warn('❌ The query may have found no jobs for this provider');
          setJobs([]);
        }
        // Handle both response.data.jobs and response.data array formats
        else {
          const jobsData = response.data.jobs || response.data;
          
          if (Array.isArray(jobsData)) {
            setJobs(jobsData);
            console.log(`✅ Loaded ${jobsData.length} jobs for schedule`);
            if (jobsData.length > 0) {
              console.log('📌 First job:', jobsData[0]);
            }
          } else {
            console.warn('⚠️ Jobs data is not an array:', jobsData);
            console.warn('📊 Response structure appears malformed');
            // Try accepted/in_progress arrays as fallback
            const accepted = response.data.accepted || [];
            const inProgress = response.data.in_progress || [];
            const allJobs = [...accepted, ...inProgress];
            setJobs(allJobs);
            console.log(`✅ Extracted ${allJobs.length} jobs from accepted/in_progress arrays`);
          }
        }
      } else {
        console.warn('⚠️ Unexpected API response format:', response);
        setJobs([]);
      }
    } catch (error) {
      console.error('❌ Error fetching jobs:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.status,
        response: error.response,
      });
      toast({
        title: 'Error',
        description: 'Failed to load schedule - please try refreshing',
        variant: 'destructive',
      });
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const getWeekDates = () => {
    const curr = new Date(currentDate);
    const first = curr.getDate() - curr.getDay();
    const week = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(curr.getFullYear(), curr.getMonth(), first + i);
      week.push(date);
    }
    return week;
  };

  const timeSlots = Array.from({ length: 13 }, (_, i) => {
    const hour = 8 + i;
    return `${String(hour).padStart(2, '0')}:00`;
  });

  const weekDates = getWeekDates();
  const dayNames = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  const getJobsForTimeSlot = (date: Date, timeSlot: string) => {
    const slotHour = parseInt(timeSlot.split(':')[0]);
    return jobs.filter(job => {
      if (!job.booking_date) {
        // If no booking_date, skip this job (it won't be displayed)
        console.warn(`⚠️ Job ${job.id} has no booking_date - cannot place on calendar`);
        return false;
      }
      
      try {
        const jobDate = new Date(job.booking_date);
        const jobHour = job.booking_time_start ? parseInt(job.booking_time_start.split(':')[0]) : 8; // Default to 8am
        const matches = jobDate.toDateString() === date.toDateString() && jobHour === slotHour;
        
        if (matches) {
          console.log(`✅ Job ${job.id} matches time slot ${date.toDateString()} at ${timeSlot}`);
        }
        
        return matches;
      } catch (error) {
        console.error(`❌ Error processing job ${job.id}:`, error);
        return false;
      }
    });
  };

  if (authLoading) {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Authenticating...</p>
          </div>
        </div>
      </ProviderLayout>
    );
  }

  if (!user || user.role !== 'provider') {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">Not authorized to access this page</p>
            <Button onClick={() => navigate('/provider/login')}>Go to Login</Button>
          </div>
        </div>
      </ProviderLayout>
    );
  }

  return (
    <ProviderLayout>
      <Helmet>
        <title>Schedule - Virewire</title>
        <meta name="description" content="Manage your job schedule" />
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Your Schedule</h1>
          <p className="text-gray-600 mt-1">Manage your accepted and in-progress jobs</p>
        </div>

        {/* Loading State */}
        {loading && (
          <Card className="bg-white">
            <CardContent className="flex justify-center items-center py-16">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
                <p className="text-gray-600">Loading your schedule...</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {!loading && jobs.length === 0 && (
          <Card className="bg-white">
            <CardContent className="flex justify-center items-center py-16">
              <div className="text-center max-w-md">
                <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <p className="text-gray-900 font-semibold mb-2">No scheduled jobs</p>
                <p className="text-sm text-gray-600 mb-6">
                  You don't have any accepted or in-progress jobs at the moment. 
                  You need to accept pending job offers first to see them here.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
                  <p className="text-xs font-semibold text-blue-900 mb-3">How to get scheduled jobs:</p>
                  <ol className="text-xs text-blue-900 space-y-2">
                    <li><span className="font-semibold">1.</span> Go to Available Jobs on your dashboard</li>
                    <li><span className="font-semibold">2.</span> Accept job offers (status: pending)</li>
                    <li><span className="font-semibold">3.</span> Jobs must have a booking date assigned</li>
                    <li><span className="font-semibold">4.</span> They'll appear in your schedule calendar</li>
                  </ol>
                </div>
                <Button 
                  onClick={() => navigate('/provider/dashboard')}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  View Available Jobs
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Calendar Card */}
        {!loading && jobs.length > 0 && (
          <Card className="bg-white">
            <CardContent className="p-8">
              {/* Header with Navigation */}
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    Week of {weekDates[0].toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })} - {weekDates[6].toLocaleDateString('en-GB', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </h2>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() - 7))}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 7))}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Hourly Timeline Calendar */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="w-20 text-left p-2"></th>
                      {weekDates.map((date, idx) => (
                        <th key={idx} className="text-center p-2 border border-gray-200">
                          <div className="text-xs font-semibold text-gray-600">{dayNames[date.getDay()]}</div>
                          <div className="text-lg font-bold text-gray-900">{date.getDate()}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {timeSlots.map((timeSlot, timeIdx) => (
                      <tr key={timeIdx}>
                        <td className="text-xs font-semibold text-gray-600 p-2 text-right pr-4">{timeSlot}</td>
                        {weekDates.map((date, dateIdx) => {
                          const slotJobs = getJobsForTimeSlot(date, timeSlot);
                          return (
                            <td key={dateIdx} className="border border-gray-200 p-2 h-16 relative">
                              {slotJobs.map((job, jobIdx) => (
                                <div
                                  key={jobIdx}
                                  onClick={() => {
                                    setSelectedJob(job);
                                    setShowJobModal(true);
                                  }}
                                  className="bg-green-600 text-white p-2 rounded text-xs font-semibold cursor-pointer hover:bg-green-700 transition-colors mb-1 relative group"
                                >
                                  {job.service_name?.substring(0, 15) || job.service?.substring(0, 15) || 'Job'}
                                  {/* Yellow dashed offer box on hover */}
                                  <div className="hidden group-hover:block absolute -top-1 -left-1 bottom-0 right-0 border-2 border-yellow-300 border-dashed rounded pointer-events-none"></div>
                                </div>
                              ))}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Job Details Modal */}
      <Dialog open={showJobModal} onOpenChange={setShowJobModal}>
        <DialogContent className="max-w-md">
          {selectedJob && (
            <div className="space-y-6 pt-4">
              <button
                onClick={() => setShowJobModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Badge */}
              <Badge className="inline-block bg-green-600 text-white">
                ACTIVE JOB
              </Badge>

              {/* Job ID and Title */}
              <div>
                <h3 className="text-2xl font-bold text-blue-600">#{selectedJob.quote_id || selectedJob.id || 'N/A'}</h3>
                <p className="text-gray-900 font-semibold mt-1">{selectedJob.service_name || selectedJob.service || 'Service'}</p>
              </div>

              {/* Yellow dashed offer box */}
              <div className="border-2 border-yellow-300 border-dashed rounded-lg p-3 bg-yellow-50">
                <p className="text-xs font-bold text-yellow-800">SCHEDULED JOB</p>
              </div>

              {/* Price and Duration */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-600 font-semibold mb-1">RATE</p>
                  <p className="text-2xl font-bold text-gray-900">£{Number(selectedJob.amount_gbp || 0).toFixed(2)}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-600 font-semibold mb-1">STATUS</p>
                  <p className="text-lg font-bold text-green-600">{(selectedJob.status || 'active').toUpperCase()}</p>
                </div>
              </div>

              {/* Date and Time */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-gray-600" />
                  <span className="text-gray-900">
                    {selectedJob.booking_date ? 
                      new Date(selectedJob.booking_date).toLocaleDateString('en-GB', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric'
                      }) : 'Date TBD'
                    }
                    {selectedJob.booking_time_start ? `, ${selectedJob.booking_time_start}` : ''}
                  </span>
                </div>
              </div>

              {/* Location */}
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <MapPin className="w-5 h-5 text-gray-600 mt-1" />
                  <div>
                    <p className="text-gray-900 font-semibold">{selectedJob.address || 'Location TBD'}</p>
                    <p className="text-sm text-gray-600">{selectedJob.postcode || ''}</p>
                  </div>
                </div>
              </div>

              {/* Customer Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-xs text-gray-600 font-semibold mb-2">CUSTOMER</p>
                <p className="text-gray-900 font-semibold">{selectedJob.customer_name || 'Unknown'}</p>
                {selectedJob.customer_email && (
                  <p className="text-sm text-gray-600">{selectedJob.customer_email}</p>
                )}
                {selectedJob.customer_phone && (
                  <p className="text-sm text-gray-600">{selectedJob.customer_phone}</p>
                )}
              </div>

              {/* Actions */}
              <div className="space-y-2">
                <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                  <MapPin className="w-4 h-4 mr-2" />
                  View on Map
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setShowJobModal(false);
                    navigate(`/provider/job/${selectedJob.id}`);
                  }}
                >
                  View Full Details
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </ProviderLayout>
  );
};

export default ProviderSchedulePage;
